/**
 * Admin Staff API client (Phase X.3.7 Slice A).
 *
 * Wraps the 11 admin-review/staff endpoints (adminStaff.ts) plus the 9
 * bio/photo endpoints (staffProfile.ts). The admin-review family gates
 * server-side on manage_staff_access (owner + exec_support); the bio/photo
 * family gates on the separate, broader manage_staff_profiles. Frontend
 * gating is convenience, not security. Mirrors src/lib/team.ts.
 */

import { writeFetch } from './writeFetch';
import { apiErrorFrom } from './api';

export interface StaffLinkRow {
  id: string;
  full_name: string;
  billable_role: string;
  // Roster/team-grouping unit (units.kind: cm_geographic for CMs,
  // service_line for SPICE). null = none set — the Team roster's "No unit
  // yet" group. Editable in the drawer (setUnit). Optional on the wire so
  // an older cached shape still renders.
  unit_id?: string | null;
  unit_name?: string | null;
  email: string | null;
  preferred_name: string | null;
  // Post-nominal credentials for the report signatory (PR-B), e.g. "BSW, CSW,
  // CMC". null = none on file.
  credentials: string | null;
  // Slice 3c — operator-settable CM bio (owner reads it via this owner-only
  // list; the broader manage_staff_profiles roles read StaffProfileRow). null
  // = none on file.
  bio: string | null;
  // 0239 — the lines printed under this person's name; null = none.
  // Optional for older shapes.
  signature_block?: string | null;
  // 0161 — CM direct phone for the client intro email. null = none on file
  // (the copied email block renders a highlighted [Phone] placeholder).
  phone: string | null;
  // Contracted weekly-hours target as numeric text ("20.00"); null = none
  // set. Optional on the wire so an older cached shape still renders.
  target_weekly_hours?: string | null;
  // Slice 3c — whether a photo is on file (drives thumbnail-vs-initials + the
  // "Remove" affordance without fetching the bytes).
  has_photo: boolean;
  // Per-staff SPICE flag (0134). true = the rate-band picker offers this
  // staffer the SPICE bands on top of whatever their role allows. Edited via
  // the People-tab "Does SPICE" toggle (manage_staff_profiles).
  does_spice: boolean;
  // Per-staff care-management flag (0143), the does_spice twin. true = the
  // picker offers the care-management bands. Edited via the People-tab "Does
  // care management" toggle.
  does_care_management: boolean;
  // Directory fields (0176), tier-projected SERVER-side for the caller: the
  // private pair (home_address, personal_phone) is null unless the caller
  // holds view_staff_private_contact. Optional to tolerate an older cached
  // wire shape; the editor reads them with ?? null.
  birthday_month?: number | null;
  birthday_day?: number | null;
  work_anniversary?: string | null;
  start_date?: string | null;
  home_address?: string | null;
  /** The home's five parts (0228); null on every part: text only. */
  home_address_line1?: string | null;
  home_address_line2?: string | null;
  home_address_city?: string | null;
  home_address_state?: string | null;
  home_address_zip?: string | null;
  home_city?: string | null;
  home_zip?: string | null;
  personal_phone?: string | null;
  // false = archived ("former staff"). The People list hides these by default;
  // a "Show former staff" toggle reveals them with a Restore action.
  is_active: boolean;
  linked_user_id: string | null;
  linked_user_email: string | null;
  linked_user_display_name: string | null;
  // Suggested email-equal match for an unlinked row (null when linked, no
  // roster email, no match, or ambiguous).
  suggested_user_id: string | null;
  suggested_user_email: string | null;
  suggested_user_display_name: string | null;
  suggested_ambiguous: boolean;
}

export interface StaffListResponse {
  rows: StaffLinkRow[];
  /** Whether the private-contact pair was served (the caller holds
   *  view_staff_private_contact); the editor shows those fields iff so. */
  private_contact_visible?: boolean;
  request_id?: string;
}

/**
 * Slice 3c — the MINIMAL CM-profile row for the bio/photo editor opened to the
 * broader manage_staff_profiles roles. Mirrors the server `StaffProfileRow`:
 * carries NO account-link status (linked/suggested user data stays on the
 * owner-only StaffLinkRow), so the non-owner editor never reads owner-only
 * People data. Since 0161 it DOES carry the professional profile fields the
 * intro-email copy block assembles from (email / preferred_name /
 * credentials / phone) — see the widening note on the server shape.
 */
export interface StaffProfileRow {
  roster_id: string;
  full_name: string;
  // The designation (care_manager / spice_therapist / ...); drives the
  // live active care-manager count in the People header.
  billable_role?: string;
  unit_id: string | null;
  unit_name: string | null;
  // 0161 — the CM's work contact email: the intro email's "Email." contact
  // line. Resolved SERVER-side as roster email first, then the linked
  // account's sign-in address (founder report 2026-07-25) — deliberately one
  // opaque field, so this shape still carries no account-link status.
  email: string | null;
  // 0161 — sign-off first name (0080): the intro email's first-name
  // derivation (preferred_name ?? first token of full_name).
  preferred_name: string | null;
  // 0161 — post-nominal credentials (0094): the intro email's credential
  // clause (null → clause omitted).
  credentials: string | null;
  bio: string | null;
  // 0239 — the lines printed under this person's name on the documents they
  // sign; null = none (the name prints alone). Optional for older shapes.
  signature_block?: string | null;
  // 0161 — direct phone (null → highlighted [Phone] placeholder).
  phone: string | null;
  // Contracted weekly-hours target as numeric text; null = none set.
  target_weekly_hours?: string | null;
  has_photo: boolean;
  // Per-staff SPICE flag (0134) — same toggle surface as the owner path.
  does_spice: boolean;
  // Per-staff care-management flag (0143) — same toggle surface.
  does_care_management: boolean;
  // Directory fields (0176), tier-projected server-side (see StaffLinkRow).
  birthday_month?: number | null;
  birthday_day?: number | null;
  work_anniversary?: string | null;
  start_date?: string | null;
  home_address?: string | null;
  /** The home's five parts (0228); null on every part: text only. */
  home_address_line1?: string | null;
  home_address_line2?: string | null;
  home_address_city?: string | null;
  home_address_state?: string | null;
  home_address_zip?: string | null;
  home_city?: string | null;
  home_zip?: string | null;
  personal_phone?: string | null;
}

export interface StaffProfilesResponse {
  profiles: StaffProfileRow[];
  /** Whether the private-contact pair was served (see StaffListResponse). */
  private_contact_visible?: boolean;
  request_id?: string;
}

/** What the save did to the capacity map's home point: a non-blank home
 *  address is geocoded server-side and, when found, becomes the point the
 *  map places this person at. Absent on an older server. */
export type HomePointOutcome = 'not_attempted' | 'cleared' | 'updated' | 'no_match' | 'unavailable';

/** A staff home as its five parts (0228), as the forms send it. */
export interface StaffHomeParts {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/** A PARTIAL patch of the directory fields (0176): present = written,
 *  present null = cleared, absent = untouched. The birthday pair travels
 *  together. */
export interface StaffDirectoryFieldsPatch {
  birthday_month?: number | null;
  birthday_day?: number | null;
  work_anniversary?: string | null;
  start_date?: string | null;
  /** The home as its five parts; null clears it. */
  home_address?: StaffHomeParts | null;
  personal_phone?: string | null;
}

// ----- Staff-roster creation + departure warning (2026-07-27 build) ----

/** A units-catalog entry (GET /admin-review/staff/create-options): the
 *  Add-a-person form's Unit picker AND the existing-person unit editor's
 *  options (drawer, setUnit) — one units catalog, both surfaces. `kind` is
 *  optional on the wire so an older cached shape still renders (falls back
 *  to the bare name — see unitOptionLabel). */
export interface UnitOption {
  id: string;
  name: string;
  kind?: 'cm_geographic' | 'service_line';
}

/** "service line" / "geographic" — the plain-word reading of a unit's kind
 *  discriminator (units.kind, db/migrations/0076). */
export function unitKindLabel(kind: 'cm_geographic' | 'service_line'): string {
  return kind === 'service_line' ? 'service line' : 'geographic';
}

/** A unit option's picker label: "SPICE · service line" for the SPICE
 *  service-line unit, "Northern Lights · geographic" for a CM unit — so a
 *  list mixing both kinds reads unambiguously. Falls back to the bare name
 *  when kind is absent (an older cached shape). */
export function unitOptionLabel(u: { name: string; kind?: 'cm_geographic' | 'service_line' }): string {
  return u.kind ? `${u.name} · ${unitKindLabel(u.kind)}` : u.name;
}

/** The Add-a-person form's picker sources (GET /admin-review/staff/
 *  create-options). `default_company_id` is the configured operating
 *  company, or null when unset (the form says so — it never guesses).
 *  ALSO the existing-person unit editor's options source (People-tab
 *  drawer). */
export interface StaffCreateOptionsResponse {
  units: UnitOption[];
  companies: Array<{ id: string; name: string }>;
  default_company_id: string | null;
  request_id?: string;
}

export interface CreateStaffRequest {
  full_name: string;
  billable_role: string;
  employment_type: 'employee' | 'contractor';
  email?: string;
  phone?: string;
  credentials?: string;
  unit_id?: string;
  /** OWNER ONLY. Present from a non-owner → the server rejects with
   *  company_override_forbidden (403), never silently drops it. Omit
   *  entirely unless the signed-in user's role is 'owner'. */
  company_id?: string;
  /** Weekly hours target for any designation, as typed ("20" or "20.5");
   *  a SPICE session is one hour. */
  target_weekly_hours?: string;
  /** The employee-card directory fields (the same names as
   *  StaffDirectoryFieldsPatch). Sent only when filled in; the birthday
   *  pair travels together. */
  birthday_month?: number;
  birthday_day?: number;
  work_anniversary?: string;
  start_date?: string;
  /** The home as its five parts; sent only when filled in. */
  home_address?: StaffHomeParts;
  personal_phone?: string;
}

export interface CreateStaffResponse {
  row: StaffLinkRow;
  /** What the map did with a home address on the create. */
  home_point?: HomePointOutcome;
  request_id?: string;
}

/** Departure warning (founder-ruled: warn with counts, never block,
 *  never auto-reassign). Counts derive from the array lengths. */
export interface ArchivePreflightResponse {
  clients: Array<{
    id: string;
    display_name: string;
    is_primary_cm: boolean;
    is_spice_therapist: boolean;
    /** An additional care manager on the client (client_staff_assignments). */
    is_additional_cm: boolean;
    /** An additional SPICE therapist on the client. */
    is_additional_spice_therapist: boolean;
    /** Named as the therapist of one of the client's sessions. */
    is_spice_session_therapist: boolean;
  }>;
  open_work_items: Array<{ id: string; title: string; kind: string }>;
  request_id?: string;
}

async function requestJson(input: string, init: RequestInit = {}): Promise<unknown> {
  const r = await writeFetch(input, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!r.ok) throw await apiErrorFrom(r, `${init.method ?? 'GET'} ${input}`);
  return r.json();
}

const base = (slug: string): string => `/api/t/${encodeURIComponent(slug)}/v1/admin-review/staff`;

// Slice 3c — the bio/photo surface lives under /v1/staff (manage_staff_profiles
// writes + view_workspace photo serve), NOT the owner-only /admin-review/ base.
const staffBase = (slug: string): string => `/api/t/${encodeURIComponent(slug)}/v1/staff`;

/**
 * The photo-serve URL for an <img src>. `version` (e.g. Date.now() after an
 * upload/remove) busts the browser cache so a replace shows immediately. On a
 * 404 (no photo) the consuming <img onError> falls back to the initials avatar.
 */
export function cmPhotoUrl(tenantSlug: string, rosterId: string, version?: string | number): string {
  const url = `${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/photo`;
  return version === undefined ? url : `${url}?v=${encodeURIComponent(String(version))}`;
}

export const staffAdminApi = {
  async list(tenantSlug: string): Promise<StaffListResponse> {
    return (await requestJson(base(tenantSlug))) as StaffListResponse;
  },

  /** `endCareTeams` is the Directory Editor door's confirm: the number of
   *  clients the office confirms the person loses access to, sent on the
   *  resubmission after the confirm and never on the first attempt. */
  async link(tenantSlug: string, rosterId: string, userId: string, endCareTeams?: number): Promise<{ linked: true }> {
    return (await requestJson(`${base(tenantSlug)}/${encodeURIComponent(rosterId)}/link`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, ...(endCareTeams === undefined ? {} : { end_care_teams: endCareTeams }) }),
    })) as { linked: true };
  },

  async unlink(tenantSlug: string, rosterId: string): Promise<{ linked: false }> {
    return (await requestJson(`${base(tenantSlug)}/${encodeURIComponent(rosterId)}/unlink`, {
      method: 'POST',
    })) as { linked: false };
  },

  async setPreferredName(
    tenantSlug: string,
    rosterId: string,
    preferredName: string,
  ): Promise<{ saved: true }> {
    return (await requestJson(
      `${base(tenantSlug)}/${encodeURIComponent(rosterId)}/preferred-name`,
      { method: 'POST', body: JSON.stringify({ preferred_name: preferredName }) },
    )) as { saved: true };
  },

  async setCredentials(
    tenantSlug: string,
    rosterId: string,
    credentials: string,
  ): Promise<{ saved: true }> {
    return (await requestJson(
      `${base(tenantSlug)}/${encodeURIComponent(rosterId)}/credentials`,
      { method: 'POST', body: JSON.stringify({ credentials }) },
    )) as { saved: true };
  },

  /** Set a staff member's designation (billable_role). The server derives the
   *  implied rate flags on a real change (care_manager/manager -> care
   *  management, spice_therapist -> SPICE). Gated on manage_staff_access. */
  async setBillableRole(
    tenantSlug: string,
    rosterId: string,
    billableRole: string,
  ): Promise<{ saved: true }> {
    return (await requestJson(
      `${base(tenantSlug)}/${encodeURIComponent(rosterId)}/billable-role`,
      { method: 'POST', body: JSON.stringify({ billable_role: billableRole }) },
    )) as { saved: true };
  },

  /** Create a roster person (owner path "Add a person"). A person with no
   *  login is the normal case; linking happens through the link flow. */
  async create(tenantSlug: string, input: CreateStaffRequest): Promise<CreateStaffResponse> {
    return (await requestJson(base(tenantSlug), {
      method: 'POST',
      body: JSON.stringify(input),
    })) as CreateStaffResponse;
  },

  /** The Add-a-person form's picker sources (units, companies, default) —
   *  ALSO the existing-person unit editor's units source (People-tab
   *  drawer). Reachable by manage_staff_access OR manage_staff_profiles. */
  async createOptions(tenantSlug: string): Promise<StaffCreateOptionsResponse> {
    return (await requestJson(`${base(tenantSlug)}/create-options`)) as StaffCreateOptionsResponse;
  },

  /** The departure warning's counts + lists, fetched when the archive
   *  confirm opens. Advisory — the archive is never conditioned on it. */
  async archivePreflight(tenantSlug: string, rosterId: string): Promise<ArchivePreflightResponse> {
    return (await requestJson(
      `${base(tenantSlug)}/${encodeURIComponent(rosterId)}/archive-preflight`,
    )) as ArchivePreflightResponse;
  },

  async archive(tenantSlug: string, rosterId: string): Promise<{ archived: true }> {
    return (await requestJson(`${base(tenantSlug)}/${encodeURIComponent(rosterId)}/archive`, {
      method: 'POST',
    })) as { archived: true };
  },

  async restore(tenantSlug: string, rosterId: string): Promise<{ restored: true }> {
    return (await requestJson(`${base(tenantSlug)}/${encodeURIComponent(rosterId)}/restore`, {
      method: 'POST',
    })) as { restored: true };
  },

  // ----- Slice 3c — CM bio + photo (manage_staff_profiles) -------------

  /** The minimal CM-profile list for the non-owner bio/photo editor. */
  async listProfiles(tenantSlug: string): Promise<StaffProfilesResponse> {
    return (await requestJson(staffBase(tenantSlug))) as StaffProfilesResponse;
  },

  /** Set or clear a CM's bio (empty string clears → NULL). */
  async setBio(tenantSlug: string, rosterId: string, bio: string): Promise<{ saved: true }> {
    return (await requestJson(`${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/bio`, {
      method: 'POST',
      body: JSON.stringify({ bio }),
    })) as { saved: true };
  },

  /** Set or clear a person's signature block (0239; blank clears → NULL).
   *  The office sets anyone's; a person sets only their own. */
  async setSignatureBlock(tenantSlug: string, rosterId: string, signatureBlock: string): Promise<{ saved: true }> {
    return (await requestJson(`${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/signature-block`, {
      method: 'POST',
      body: JSON.stringify({ signature_block: signatureBlock }),
    })) as { saved: true };
  },

  /** Set or clear a CM's direct phone (0161; empty string clears → NULL). */
  // 0176 — the directory fields, one partial patch per save.
  async setDirectoryFields(
    tenantSlug: string,
    rosterId: string,
    patch: StaffDirectoryFieldsPatch,
  ): Promise<{ saved: true; home_point?: HomePointOutcome; home_precision?: 'street' | 'zip' | 'none' }> {
    return (await requestJson(
      `${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/directory-fields`,
      { method: 'POST', body: JSON.stringify(patch) },
    )) as { saved: true; home_point?: HomePointOutcome; home_precision?: 'street' | 'zip' | 'none' };
  },

  async setPhone(tenantSlug: string, rosterId: string, phone: string): Promise<{ saved: true }> {
    return (await requestJson(`${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/phone`, {
      method: 'POST',
      body: JSON.stringify({ phone }),
    })) as { saved: true };
  },

  /** Set or clear a roster person's unit (null clears it). Any active unit
   *  is accepted regardless of kind or this person's designation — the
   *  server never pairs a unit's kind to a role. */
  async setUnit(
    tenantSlug: string,
    rosterId: string,
    unitId: string | null,
  ): Promise<{ saved: true }> {
    return (await requestJson(`${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/unit`, {
      method: 'POST',
      body: JSON.stringify({ unit_id: unitId }),
    })) as { saved: true };
  },

  /** Set or clear the contracted weekly-hours target (empty string clears → NULL). */
  async setTargetWeeklyHours(tenantSlug: string, rosterId: string, targetWeeklyHours: string): Promise<{ saved: true }> {
    return (await requestJson(`${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/target-weekly-hours`, {
      method: 'POST',
      body: JSON.stringify({ target_weekly_hours: targetWeeklyHours }),
    })) as { saved: true };
  },

  /** Set the per-staff SPICE flag (0134) — the People-tab "Does SPICE" toggle. */
  async setDoesSpice(
    tenantSlug: string,
    rosterId: string,
    doesSpice: boolean,
  ): Promise<{ saved: true }> {
    return (await requestJson(
      `${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/does-spice`,
      { method: 'POST', body: JSON.stringify({ does_spice: doesSpice }) },
    )) as { saved: true };
  },

  /** Set the per-staff care-management flag (0143) — the People-tab "Does care
   *  management" toggle. */
  async setDoesCareManagement(
    tenantSlug: string,
    rosterId: string,
    doesCareManagement: boolean,
  ): Promise<{ saved: true }> {
    return (await requestJson(
      `${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/does-care-management`,
      { method: 'POST', body: JSON.stringify({ does_care_management: doesCareManagement }) },
    )) as { saved: true };
  },

  /** Upload/replace a CM's photo. `data` is the base64 of the raw image bytes. */
  async uploadPhoto(
    tenantSlug: string,
    rosterId: string,
    data: string,
    mime: string,
  ): Promise<{ uploaded: true }> {
    return (await requestJson(`${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/photo`, {
      method: 'PUT',
      body: JSON.stringify({ data, mime }),
    })) as { uploaded: true };
  },

  /** Remove a CM's photo (→ initials fallback). */
  async deletePhoto(tenantSlug: string, rosterId: string): Promise<{ deleted: true }> {
    return (await requestJson(`${staffBase(tenantSlug)}/${encodeURIComponent(rosterId)}/photo`, {
      method: 'DELETE',
    })) as { deleted: true };
  },
};

